const RAW_API_BASE_URL = process.env.NEXT_PUBLIC_API_URL?.trim() ?? "";
export const API_BASE_URL = RAW_API_BASE_URL ? RAW_API_BASE_URL.replace(/\/$/, "") : "";

const withApiPath = (path: string) => API_BASE_URL ? `${API_BASE_URL}${path}` : path;

// Auth token management
let authToken: string | null = typeof window !== "undefined" ? localStorage.getItem("auth_token") : null;

export const setAuthToken = (token: string | null) => {
  authToken = token;
  if (typeof window !== "undefined") {
    if (token) {
      localStorage.setItem("auth_token", token);
    } else {
      localStorage.removeItem("auth_token");
    }
  }
};

export const getAuthToken = (): string | null => {
  // Fall back to localStorage if the module-level cache is empty.
  // Background: AuthTokenSync (in app/providers.tsx) is what normally
  // hydrates `authToken` from the NextAuth session via setAuthToken().
  // But its useEffect runs AFTER the first render. If a component's
  // mount effect fires apiFetch before AuthTokenSync runs (sidebar
  // credit-balance hook is the common case), `authToken` is still null
  // and we'd send the request without an Authorization header. The
  // backend correctly 401s, and handleSessionExpired then logs the
  // user out — even though they just successfully logged in.
  //
  // The token IS already in localStorage at that point (login flow
  // writes it), so re-reading repairs the race. Keep the module
  // cache for hot-path callers so we're not hitting localStorage on
  // every API call.
  if (authToken === null && typeof window !== "undefined") {
    const fromStorage = localStorage.getItem("auth_token");
    if (fromStorage) {
      authToken = fromStorage;
    }
  }
  return authToken;
};

// Guard: prevent multiple concurrent sign-out + redirect cycles when
// several parallel API calls all return 401 at the same time.
let _signingOut = false;

async function handleSessionExpired() {
  if (_signingOut) return;          // already in progress
  _signingOut = true;
  setAuthToken(null);

  try {
    // Destroy the NextAuth session cookie so the middleware no longer
    // considers the user "logged in". Without this, the middleware
    // bounces the user from /login back to /dashboard → reload loop.
    const { signOut } = await import("next-auth/react");
    await signOut({ redirect: false });
  } catch {
    // signOut may fail if the session is already gone; that's fine.
  }

  if (window.location.pathname !== "/login" && window.location.pathname !== "/client/login") {
    window.location.href = "/login?error=SessionExpired";
  }
  // Don't reset _signingOut — we're navigating away; a fresh page load
  // resets all module-level state.
}

// Base fetch wrapper — exported so callers can hit endpoints that
// don't have a dedicated helper yet (e.g. /api/auth/me, ad-hoc admin
// reads) without each having to re-implement auth + 401 handling.
export const apiFetch = async (url: string, options: RequestInit = {}) => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((options.headers as Record<string, string>) || {}),
  };

  // getAuthToken() re-reads localStorage when the module cache is empty
  // (handles the AuthTokenSync race on fresh mount). Capture whether we
  // actually sent a credential so the 401 handler below can distinguish
  // "token rejected" (real session expiry) from "request went out without
  // a token" (timing race — not a session-expiry).
  const tokenSent = !!getAuthToken();
  if (tokenSent) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  const response = await fetch(withApiPath(url), {
    ...options,
    headers,
  });

  if (!response.ok) {
    // Only 401 means "token invalid/expired" — log out and bounce to /login.
    // 403 means "authenticated but forbidden" (admin-only endpoint, feature
    // disabled, tenant mismatch, etc.) — those are valid sessions hitting a
    // wrong resource and must NOT trigger logout. Let the caller surface the
    // error via toast instead.
    //
    // Crucially, ONLY trigger session-expired if we actually sent a token.
    // A 401 on an anonymous request just means "missing credentials" — it
    // is NOT a signal that the user's session expired. Without this guard,
    // a mount-time race where apiFetch fires before AuthTokenSync hydrates
    // the in-memory token would log the user out moments after they signed
    // in. (Yes, this happened.)
    if (response.status === 401 && tokenSent && typeof window !== "undefined") {
      handleSessionExpired();
    }

    let detail = response.statusText;
    try {
      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const errorBody = await response.json();
        if (errorBody?.detail) {
          if (typeof errorBody.detail === "string") {
            detail = errorBody.detail;
          } else if (Array.isArray(errorBody.detail)) {
            detail = errorBody.detail.join("; ");
          } else {
            detail = JSON.stringify(errorBody.detail);
          }
        }
      } else {
        const text = await response.text();
        if (text) {
          detail = text;
        }
      }
    } catch {
      // Ignore parsing errors for best-effort message extraction.
    }

    throw new Error(`API call failed: ${response.status} ${detail}`);
  }

  // Handle empty responses
  const contentLength = response.headers.get("content-length");
  if (contentLength === "0" || response.status === 204) {
    return null;
  }

  return response.json();
};

// Document API
export const getDocuments = async (params: {
  page?: number;
  page_size?: number;
  status?: string;
  client_id?: string;
} = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      query.append(key, value.toString());
    }
  });
  return apiFetch(`/api/documents?${query}`);
};

export const getDocument = async (id: string) => {
  return apiFetch(`/api/documents/${id}`);
};

export const uploadDocument = async (file: File, clientId?: string, notes?: string) => {
  const formData = new FormData();
  formData.append("file", file);
  if (clientId) formData.append("client_id", clientId);
  if (notes) formData.append("notes", notes);

  const headers: Record<string, string> = {};
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  const response = await fetch(withApiPath(`/api/documents/upload`), {
    method: "POST",
    body: formData,
    headers,
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

export const reprocessDocument = async (id: string) => {
  return apiFetch(`/api/documents/${id}/reprocess`, { method: "POST" });
};

export const deleteDocument = async (id: string) => {
  return apiFetch(`/api/documents/${id}`, { method: "DELETE" });
};

export const getDocumentFileUrl = (id: string) => {
  return withApiPath(`/api/documents/${id}/file`);
};

// Client-portal-side document helpers — these hit /api/client/documents/...
// which authenticates via the client session (the bookkeeper-side endpoints
// reject client tokens).
export const getClientDocument = async (id: string) => {
  return apiFetch(`/api/client/documents/${id}`);
};

export const getClientDocumentFileUrl = (id: string) => {
  return withApiPath(`/api/client/documents/${id}/file`);
};

// Client API
//
// Accepts EITHER the legacy positional form (page, pageSize, activeOnly,
// includeUnassigned) that ``useClients`` still uses, or a params object.
// Without this, callers that were never migrated silently dropped their
// page_size and got the backend default (20), which broke client-pickers
// on stacks with more than 20 clients (e.g. prod).
export const getClients = async (
  pageOrParams:
    | number
    | {
        page?: number;
        page_size?: number;
        is_active?: boolean;
        active_only?: boolean; // legacy alias accepted for backwards compat
        include_unassigned?: boolean;
        bookkeeper_id?: string; // admin-only filter (silently ignored for non-admins)
        search?: string;
      } = {},
  pageSize?: number,
  activeOnly?: boolean,
  includeUnassigned?: boolean,
) => {
  const raw =
    typeof pageOrParams === "number"
      ? {
          page: pageOrParams,
          page_size: pageSize,
          active_only: activeOnly,
          include_unassigned: includeUnassigned,
        }
      : pageOrParams;
  // The backend GET /api/clients expects ``is_active`` (FastAPI Query
  // param) — callers historically used ``active_only`` because of the
  // hook signature. Semantics: active_only=true → only active rows;
  // active_only=false → ALL rows (NOT "only inactive"). Map only the
  // true case to is_active=true; leave is_active unset otherwise so
  // the backend's "any" path runs.
  const params: Record<string, unknown> = { ...raw };
  if ("active_only" in params) {
    if (params.active_only === true && params.is_active === undefined) {
      params.is_active = true;
    }
    delete params.active_only;
  }
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      query.append(key, value.toString());
    }
  });
  return apiFetch(`/api/clients?${query}`);
};

export const getClient = async (id: string) => {
  return apiFetch(`/api/clients/${id}`);
};

export const createClient = async (data: {
  name: string;
  email?: string | null;
  // Admin-only: assign to a specific bookkeeper. Non-admin callers ignore.
  bookkeeper_id?: string | null;
}) => {
  return apiFetch("/api/clients", {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export const updateClient = async (id: string, data: any) => {
  return apiFetch(`/api/clients/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
};

export const deleteClient = async (id: string) => {
  return apiFetch(`/api/clients/${id}`, { method: "DELETE" });
};

export const getClientFeatures = async (id: string) => {
  return apiFetch(`/api/clients/${id}/features`);
};

export const updateClientFeatures = async (id: string, features: Record<string, boolean>) => {
  return apiFetch(`/api/clients/${id}/features`, {
    method: "PUT",
    body: JSON.stringify({ features }),
  });
};

export const getMyClientFeatures = async () => {
  return apiFetch("/api/client/features");
};

// Booking API
export const getBookings = async (params: {
  page?: number;
  page_size?: number;
  status?: string;
  client_id?: string;
} = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      query.append(key, value.toString());
    }
  });
  return apiFetch(`/api/bookings?${query}`);
};

export const getBooking = async (id: string) => {
  return apiFetch(`/api/bookings/${id}`);
};

export const approveBooking = async (id: string) => {
  return apiFetch(`/api/bookings/${id}/approve`, {
    method: "POST",

  });
};

export const rejectBooking = async (id: string) => {
  return apiFetch(`/api/bookings/${id}/reject`, { method: "POST" });
};

// Export API
export const getExportUrl = (format: string, params: any = {}) => {
  const query = new URLSearchParams(params);
  return withApiPath(`/api/export/${format}?${query}`);
};

// Credits API
export const getCreditBalance = async () => {
  return apiFetch("/api/credits/balance");
};

export const getCreditsHistory = async (params: {
  page?: number;
  page_size?: number;
} = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      query.append(key, value.toString());
    }
  });
  return apiFetch(`/api/credits/history?${query}`);
};

export const purchaseCredits = async (data: {
  amount: number;
  payment_method_id: string;
}) => {
  return apiFetch("/api/credits/purchase", {
    method: "POST",
    body: JSON.stringify(data),
  });
};

// Client Portal API (authenticated client endpoints)
export interface ClientProfile {
  id: string;
  name: string;
  email: string | null;
  bookkeeper_id: string | null;
  is_active: boolean;
  created_at: string;
  last_login_at: string | null;
  has_login: boolean;
}

export const getClientProfile = async (): Promise<ClientProfile> => {
  return apiFetch("/api/client/me");
};

export const getClientDocuments = async (page: number = 1, pageSize: number = 20) => {
  const query = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString(),
  });
  return apiFetch(`/api/client/documents?${query}`);
};

export const uploadClientDocument = async (file: File) => {
  const formData = new FormData();
  formData.append("file", file);

  const headers: Record<string, string> = {};
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  const response = await fetch(`${API_BASE_URL}/api/client/documents/upload`, {
    method: "POST",
    body: formData,
    headers,
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
  }

  return response.json();
};


// Public/Client API
/**
 * Fetch the public client info for the upload landing page at /c/{token}.
 * Backend route: GET /api/public/upload/{token} (see backend/app/routers/public.py).
 */
export const getClientInfo = async (token: string): Promise<{ name: string; is_active: boolean }> => {
  return apiFetch(`/api/public/upload/${token}`);
};

/**
 * Submit a PDF via the tokenized public upload endpoint.
 * Backend route: POST /api/public/upload/{token}.
 */
export const publicUpload = async (token: string, file: File, notes?: string) => {
  const formData = new FormData();
  formData.append("file", file);
  if (notes) formData.append("notes", notes);

  const response = await fetch(`${API_BASE_URL}/api/public/upload/${token}`, {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// Invite API
export interface InviteValidation {
  valid: boolean;
  client_name?: string | null;
  client_email?: string | null;
  bookkeeper_name?: string | null;
  message?: string;
}

export const validateInvite = async (token: string): Promise<InviteValidation> => {
  return apiFetch(`/api/client/invite/${token}`);
};

export const acceptInvite = async (token: string, password: string) => {
  return apiFetch(`/api/client/invite/${token}/accept`, {
    method: "POST",
    body: JSON.stringify({ password }),
  });
};

// Password reset
export const requestPasswordReset = async (email: string) => {
  return apiFetch("/api/client/password-reset", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
};

// Cost estimation
export const estimateTicketCost = async (subject: string, body: string) => {
  return apiFetch("/api/client/tickets/estimate", {
    method: "POST",
    body: JSON.stringify({ subject, body }),
  });
};

export const estimateAppointmentCost = async (date: string, startTime: string, endTime: string, topic?: string) => {
  return apiFetch("/api/client/appointments/estimate", {
    method: "POST",
    body: JSON.stringify({ date, start_time: startTime, end_time: endTime, topic }),
  });
};

// Placeholder functions for missing imports
export const getPayments = async () => {
  return apiFetch("/api/admin/payments");
};

// Additional client functions
export const regenerateClientToken = async (id: string) => {
  return apiFetch(`/api/clients/${id}/regenerate-token`, { method: "POST" });
};

export const claimClient = async (id: string) => {
  return apiFetch(`/api/clients/${id}/claim`, { method: "POST" });
};

// Booking line updates
export const updateBookingLine = async (bookingId: string, lineId: string, data: any) => {
  return apiFetch(`/api/bookings/${bookingId}/lines/${lineId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
};

// Extraction updates — backend exposes this under /api/bookings, not /api/documents
export const updateExtraction = async (documentId: string, data: any) => {
  return apiFetch(`/api/bookings/${documentId}/extraction`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
};

// Types for credits
export interface CreditBalance {
  current_balance: number;
  currency: string;
}

export interface TopupPackage {
  id: string;
  name: string;
  credits: number;
  price: number;
  currency: string;
  popular?: boolean;
}

// Additional credit functions
export const getTopupPackages = async (): Promise<TopupPackage[]> => {
  return apiFetch("/api/credits/packages");
};

export const createTopupCheckout = async (data: {
  package_id: string;
  return_url?: string;
  cancel_url?: string;
}) => {
  return apiFetch("/api/credits/checkout", {
    method: "POST",
    body: JSON.stringify(data),
  });
};

// Approval workflow
export interface PendingInvoice {
  id: string;
  filename: string;
  status: "pending" | "processing" | "ready" | "error";
  uploaded_at: string;
  client_id: string | null;
  client_name: string | null;
  vendor_name: string | null;
  invoice_number: string | null;
  gross_amount: number | null;
  invoice_date: string | null;
  error_message: string | null;
  uploaded_by_email: string | null;
  uploaded_by_name: string | null;
  intake_channel: string | null;
}

export const getPendingInvoices = async (
  clientId?: string,
  bookkeeperId?: string,
) => {
  const qs = new URLSearchParams();
  if (clientId) qs.set("client_id", clientId);
  if (bookkeeperId) qs.set("bookkeeper_id", bookkeeperId);
  const params = qs.toString() ? `?${qs.toString()}` : "";
  return apiFetch(`/api/approval/pending${params}`);
};

export const approveInvoice = async (id: string) => {
  return apiFetch(`/api/approval/${id}/approve`, { method: "POST" });
};

export const rejectInvoice = async (id: string, reason?: string) => {
  const params = reason ? `?reason=${encodeURIComponent(reason)}` : "";
  return apiFetch(`/api/approval/${id}/reject${params}`, { method: "POST" });
};

export const bulkApproveReject = async (documentIds: string[], action: string, reason?: string) => {
  return apiFetch("/api/approval/bulk", {
    method: "POST",
    body: JSON.stringify({ document_ids: documentIds, action, reason }),
  });
};

// ── Settings Credits page imports ──

export interface CreditTransaction {
  id: string;
  transaction_type: string;
  amount: number;
  balance_after: number;
  description: string | null;
  payment_amount_cents: number | null;
  created_at: string;
}

export const getCreditTransactions = async (page: number = 1, pageSize: number = 20) => {
  const query = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString(),
  });
  return apiFetch(`/api/credits/transactions?${query}`);
};

export const updateAutoRechargeSettings = async (data: {
  enabled: boolean;
  threshold: number;
  amount: number;
}) => {
  return apiFetch("/api/credits/save-payment-method", {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export const grantDemoCredits = async (amount: number, description?: string) => {
  return apiFetch("/api/credits/demo/grant", {
    method: "POST",
    body: JSON.stringify({ amount, description }),
  });
};

export const createAutoRechargeSetup = async () => {
  return apiFetch("/api/credits/setup-auto-recharge", { method: "POST" });
};

// ── Client Credits page imports ──

export interface ClientCreditBalance {
  balance: number;
  currency: string;
  auto_recharge_enabled: boolean;
  auto_recharge_threshold: number;
  auto_recharge_amount: number;
  has_payment_method: boolean;
}

export interface ClientTopupPackage {
  id: string;
  name: string;
  amount: number;
  price_cents: number;
  currency: string;
}

export interface ClientCreditTransaction {
  id: string;
  transaction_type: string;
  amount: number;
  balance_after: number;
  description: string | null;
  created_at: string;
}

export const getClientCreditBalance = async (): Promise<ClientCreditBalance> => {
  return apiFetch("/api/client/credits/balance");
};

export const getClientTopupPackages = async (): Promise<ClientTopupPackage[]> => {
  return apiFetch("/api/client/credits/packages");
};

export const getClientCreditTransactions = async (page: number = 1, pageSize: number = 20) => {
  const query = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString(),
  });
  return apiFetch(`/api/client/credits/transactions?${query}`);
};

export const createClientTopupCheckout = async (packageId: string) => {
  return apiFetch("/api/client/credits/topup", {
    method: "POST",
    body: JSON.stringify({ package_id: packageId }),
  });
};

export const updateClientAutoRechargeSettings = async (data: {
  enabled: boolean;
  threshold: number;
  amount: number;
}) => {
  return apiFetch("/api/client/credits/save-payment-method", {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export const grantClientDemoCredits = async (amount: number, description?: string) => {
  return apiFetch("/api/client/credits/demo/grant", {
    method: "POST",
    body: JSON.stringify({ amount, description }),
  });
};

export const createClientAutoRechargeSetup = async () => {
  return apiFetch("/api/client/credits/setup-auto-recharge", { method: "POST" });
};

// ── Export page imports ──

export interface ExportableInvoice {
  id: string;
  filename: string;
  client_name: string | null;
  vendor_name: string | null;
  invoice_number: string | null;
  invoice_date: string | null;
  gross_amount: number | null;
  approved_at: string | null;
}

export interface ExportBatch {
  id: string;
  created_at: string;
  document_count: number;
  status: string;
}

export interface ExportCostPreview {
  document_count: number;
  cost_per_document: number;
  total_cost: number;
  current_balance: number;
  sufficient_balance: boolean;
}

export const getExportableInvoices = async (
  clientId?: string,
  bookkeeperId?: string,
) => {
  const qs = new URLSearchParams();
  if (clientId) qs.set("client_id", clientId);
  if (bookkeeperId) qs.set("bookkeeper_id", bookkeeperId);
  const query = qs.toString() ? `?${qs.toString()}` : "";
  return apiFetch(`/api/export/available${query}`);
};

export const getExportBatches = async (bookkeeperId?: string) => {
  const query = bookkeeperId ? `?bookkeeper_id=${bookkeeperId}` : "";
  return apiFetch(`/api/export/batches${query}`);
};

export const createBatchExport = async (documentIds: string[]) => {
  return apiFetch("/api/export/batch", {
    method: "POST",
    body: JSON.stringify({ document_ids: documentIds }),
  });
};

export const deleteExportBatch = async (batchId: string) => {
  return apiFetch(`/api/export/batch/${batchId}`, { method: "DELETE" });
};

export const downloadBatchExport = async (batchId: string) => {
  const url = withApiPath(`/api/export/batch/${batchId}/download`);
  const headers: Record<string, string> = {};
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }
  const response = await fetch(url, { headers });
  if (!response.ok) throw new Error(`Download failed: ${response.status}`);
  const blob = await response.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  const disposition = response.headers.get("content-disposition");
  a.download = disposition?.match(/filename="?(.+?)"?$/)?.[1] || `export-${batchId}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

export const previewExportCost = async (documentIds: string[]) => {
  return apiFetch("/api/export/batch/preview", {
    method: "POST",
    body: JSON.stringify({ document_ids: documentIds }),
  });
};

// ── Admin Payments page imports ──

export interface AuditTransaction {
  id: string;
  bookkeeper_id: string;
  bookkeeper_email: string;
  transaction_type: string;
  amount: number;
  balance_after: number;
  description: string | null;
  created_at: string;
}

export interface WebhookLog {
  id: string;
  event_type: string;
  status: string;
  payload: any;
  error: string | null;
  created_at: string;
}

export const getAuditTransactions = async (params: {
  page?: number;
  page_size?: number;
  bookkeeper_id?: string;
} = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) query.append(key, value.toString());
  });
  return apiFetch(`/api/admin/payments/transactions?${query}`);
};

export const getWebhookLogs = async (params: {
  page?: number;
  page_size?: number;
} = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) query.append(key, value.toString());
  });
  return apiFetch(`/api/admin/payments/webhook-logs?${query}`);
};

export const adjustBookkeeperBalance = async (bookkeeperID: string, data: {
  amount: number;
  description: string;
}) => {
  return apiFetch("/api/admin/payments/adjust-balance", {
    method: "POST",
    body: JSON.stringify({ bookkeeper_id: bookkeeperID, ...data }),
  });
};

// Analytics API
export interface CategoryBreakdown {
  category: string;
  amount: number;
  count: number;
}

export interface MonthlyTotals {
  month: string;
  income: number;
  expenses: number;
  net: number;
}

export interface DashboardData {
  category_breakdown: CategoryBreakdown[];
  monthly_totals: MonthlyTotals[];
}

export const getDashboardAnalytics = async (
  startDate?: string,
  endDate?: string,
  clientId?: string,
  bookkeeperId?: string,
): Promise<DashboardData> => {
  const params = new URLSearchParams();
  if (startDate) params.append("start_date", startDate);
  if (endDate) params.append("end_date", endDate);
  if (clientId) params.append("client_id", clientId);
  if (bookkeeperId) params.append("bookkeeper_id", bookkeeperId);

  const url = `/api/analytics/dashboard${
    params.toString() ? `?${params.toString()}` : ""
  }`;

  return apiFetch(url);
};

// ---- Operational overview (inbox-first dashboard) ----
export interface OperationalKpis {
  approval_queue: number;
  failed_docs: number;
  processing_docs: number;
  open_tickets: number;
  appointments_today: number;
  idle_clients: number;
  total_clients: number;
}

export interface ClientSummary {
  id: string;
  name: string;
  email: string;
  is_active: boolean;
  document_count: number;
  documents_30d: number;
  pending_approval: number;
  failed_docs: number;
  last_upload_at: string | null;
  open_tickets: number;
  income_ytd: string;   // Decimals come over as strings
  expenses_ytd: string;
}

export interface OverviewResponse {
  kpis: OperationalKpis;
  clients: ClientSummary[];
}

export const getOverview = async (
  bookkeeperId?: string,
): Promise<OverviewResponse> => {
  const url = bookkeeperId
    ? `/api/analytics/overview?bookkeeper_id=${bookkeeperId}`
    : "/api/analytics/overview";
  return apiFetch(url);
};


export interface ClientTransaction {
  id: string;
  booking_date: string;
  amount: string;
  currency: string;
  counterpart_name: string | null;
  reference: string | null;
  purpose: string | null;
  is_matched: boolean;
  account_id: string;
  account_name: string | null;
  connection_id: string | null;
  connection_bank_name: string | null;
  receipt_status: "missing" | "uploaded" | "approved";
  document_id: string | null;
  document_filename: string | null;
}

export interface ClientTransactionList {
  items: ClientTransaction[];
  total: number;
  page: number;
  page_size: number;
}

export interface TransactionFilters {
  matched?: boolean;
  date_from?: string;
  date_to?: string;
  search?: string;
  amount_min?: string;
  amount_max?: string;
  account_id?: string;
  connection_id?: string;
}

export const getClientTransactions = async (
  page: number = 1,
  pageSize: number = 50,
  filters: TransactionFilters = {},
): Promise<ClientTransactionList> => {
  const query = new URLSearchParams({ page: page.toString(), page_size: pageSize.toString() });
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") query.append(key, String(value));
  });
  return apiFetch(`/api/client/transactions?${query}`);
};

export const uploadTransactionReceipt = async (transactionId: string, file: File) => {
  const formData = new FormData();
  formData.append("file", file);
  const headers: Record<string, string> = {};
  if (authToken) headers.Authorization = `Bearer ${authToken}`;
  const response = await fetch(
    `${API_BASE_URL}/api/client/transactions/${transactionId}/upload`,
    { method: "POST", body: formData, headers },
  );
  if (!response.ok) throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
  return response.json();
};

export const uploadOutOfPocketExpense = async (
  file: File,
  paymentMethod: string = "cash",
  description?: string,
) => {
  const formData = new FormData();
  formData.append("file", file);
  const params = new URLSearchParams({ payment_method: paymentMethod });
  if (description) params.append("description", description);
  const headers: Record<string, string> = {};
  if (authToken) headers.Authorization = `Bearer ${authToken}`;
  const response = await fetch(
    `${API_BASE_URL}/api/client/expenses/out-of-pocket?${params}`,
    { method: "POST", body: formData, headers },
  );
  if (!response.ok) throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
  return response.json();
};

// --- Client Bank Connections ---

export interface ClientBankConnection {
  id: string;
  bank_name: string;
  provider: string;
  status: string;
  created_at: string;
}

export interface ClientBankConnectionCreateResponse extends ClientBankConnection {
  web_form_url?: string;
  account_id: string;
  transactions_synced: number;
}

export interface ClientBankingFeatureStatus {
  banking_enabled: boolean;
  mock_sync: boolean;
}

export const getClientBankingFeatureStatus = async (): Promise<ClientBankingFeatureStatus> => {
  return apiFetch("/api/client/bank-connections/feature-status");
};

export const getClientBankConnections = async (): Promise<ClientBankConnection[]> => {
  return apiFetch("/api/client/bank-connections");
};

export const createClientBankConnection = async (bankName: string): Promise<ClientBankConnectionCreateResponse> => {
  return apiFetch("/api/client/bank-connections", {
    method: "POST",
    body: JSON.stringify({ bank_name: bankName }),
  });
};

export const deleteClientBankConnection = async (id: string): Promise<void> => {
  await apiFetch(`/api/client/bank-connections/${id}`, { method: "DELETE" });
};

export const syncClientBankConnection = async (id: string): Promise<{ status: string; accounts?: number; transactions?: number; message?: string }> => {
  return apiFetch(`/api/client/bank-connections/${id}/sync`, { method: "POST" });
};
export interface ClientBankConnectionSyncRangeRequest {
  date_from?: string;
  date_to?: string;
}

export interface ClientBankConnectionSyncRangeResponse {
  fetched_transactions: number;
  new_transactions: number;
  existing_transactions: number;
  new_matches: number;
  date_from?: string;
  date_to?: string;
  note?: string;
}

export interface ClearClientConnectionTransactionsResponse {
  deleted_transactions: number;
}

export const clearClientConnectionTransactions = async (
  id: string,
): Promise<ClearClientConnectionTransactionsResponse> => {
  return apiFetch(`/api/client/bank-connections/${id}/transactions`, {
    method: "DELETE",
  });
};

export const syncClientBankConnectionRange = async (
  id: string,
  payload: ClientBankConnectionSyncRangeRequest,
): Promise<ClientBankConnectionSyncRangeResponse> => {
  return apiFetch(`/api/client/bank-connections/${id}/sync-range`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

